import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, Box, Switch, FormControlLabel } from "@mui/material";
import { CloverService } from "../../services/clover-service";
import { useToast } from "../../context/toast-context";
import { Team } from "../icons/Team";
import { LoadingModal } from "../LoadingModal";

interface ModalCloverPaymentEmployeeToggleProps {
  branchId: number;
  considerPaymentEmployee?: boolean;
  onToggle?: (branchId: number, newValue: boolean) => void;
}

const confirmationModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "400px",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

export function ModalCloverPaymentEmployeeToggle({
  branchId,
  considerPaymentEmployee = false,
  onToggle,
}: ModalCloverPaymentEmployeeToggleProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);

  const { t } = useTranslation();
  const { showToast } = useToast();
  const cloverService = useMemo(() => new CloverService(), []);

  const handleToggle = async () => {
    if (pendingValue === null) return;

    setIsLoading(true);
    try {
      await cloverService.updateConsiderPaymentEmployee(
        branchId,
        pendingValue,
        () => {
          showToast(
            pendingValue
              ? t("branches.clover-payment-employee.enabled")
              : t("branches.clover-payment-employee.disabled"),
            "success"
          );
          onToggle?.(branchId, pendingValue);
          setIsConfirmationModalOpen(false);
          setIsModalOpen(false);
          setPendingValue(null);
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchChange = (newValue: boolean) => {
    if (newValue !== considerPaymentEmployee) {
      setPendingValue(newValue);
      setIsConfirmationModalOpen(true);
    }
  };

  return (
    <>
      <Button
        title={t("branches.clover-payment-employee.label")}
        onClick={() => setIsModalOpen(true)}
        sx={{
          margin: 0,
          minWidth: "fit-content",
          background: considerPaymentEmployee ? "#EF0BB8" : "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: "2.5rem",
          height: "2.5rem",
          padding: "6px",
          "&:hover": {
            background: considerPaymentEmployee ? "#d40aa3" : "rgba(239, 11, 184, 0.08)",
          },
        }}
      >
        <Team stroke={considerPaymentEmployee ? "#fff" : "#EF0BB8"} />
      </Button>

      <Modal
        open={isModalOpen}
        onClose={() => {
          if (!isLoading) {
            setIsModalOpen(false);
            setPendingValue(null);
          }
        }}
        aria-labelledby="clover-payment-employee-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 520,
            bgcolor: "#fff",
            border: "1px solid #898989",
            boxShadow: 24,
            borderRadius: 4,
          }}
        >
          <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
            <h1>{t("branches.clover-payment-employee.modal-title")}</h1>
          </div>
          <div className="p-8">
            <p className="text-sm text-gray-600 mb-6 text-center">
              {t("branches.clover-payment-employee.modal-description")}
            </p>
            <div className="flex justify-center">
              <FormControlLabel
                sx={{
                  "& .MuiTypography-root": {
                    fontFamily: "Montserrat",
                    color: "#000",
                    fontSize: "16px",
                    fontWeight: "500",
                  },
                }}
                control={
                  <Switch
                    checked={considerPaymentEmployee}
                    onChange={(e) => handleSwitchChange(e.target.checked)}
                    disabled={isLoading}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
                        bgcolor: "#EF0BB8",
                      },
                      "& .MuiSwitch-track": {
                        bgcolor: "#898989",
                      },
                      "& .MuiSwitch-thumb": {
                        bgcolor: "#EF0BB8",
                      },
                    }}
                  />
                }
                label={
                  considerPaymentEmployee
                    ? t("branches.clover-payment-employee.enable")
                    : t("branches.clover-payment-employee.disable")
                }
              />
            </div>
          </div>
        </Box>
      </Modal>

      <Modal
        open={isConfirmationModalOpen}
        onClose={() => {
          if (!isLoading) {
            setIsConfirmationModalOpen(false);
            setPendingValue(null);
          }
        }}
        aria-labelledby="clover-payment-employee-confirmation"
      >
        <Box sx={confirmationModalStyle}>
          <div className="bg-gray-light text-center text-xl font-normal text-black py-6 px-2">
            <h1>{t("branches.clover-payment-employee.confirmation-title")}</h1>
          </div>
          <div className="p-6 flex flex-col gap-6">
            <p className="text-sm text-gray-700 text-center">
              {pendingValue
                ? t("branches.clover-payment-employee.confirmation-enable")
                : t("branches.clover-payment-employee.confirmation-disable")}
            </p>
            <div className="flex justify-center gap-4">
              <Button
                onClick={() => {
                  setIsConfirmationModalOpen(false);
                  setPendingValue(null);
                }}
                disabled={isLoading}
                sx={{
                  color: "#000",
                  fontFamily: "Montserrat",
                  textTransform: "none",
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleToggle}
                disabled={isLoading}
                sx={{
                  backgroundColor: "#EF0BB8",
                  color: "#fff",
                  fontFamily: "Montserrat",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#d40aa3" },
                }}
              >
                {t("branches.clover-payment-employee.confirm")}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      <LoadingModal isOpen={isLoading} />
    </>
  );
}
